import AsyncStorage from '@react-native-async-storage/async-storage';
import { router, usePathname, type Href } from 'expo-router';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { CollectionsLetterModal } from '@/components/accounting/collections-letter-modal';
import { BoardApprovalModal } from '@/components/applications/board-approval-modal';
import { NeedsInfoModal } from '@/components/applications/needs-info-modal';
import { useSession } from '@/components/auth';
import { ForwardModal } from '@/components/inbox/forward-modal';
import { ReplyModal } from '@/components/inbox/reply-modal';
import { useNotification } from '@/components/notification-system';
import { draftStorageKey, purgeForeignComposerDrafts } from '@/hooks/use-composer-draft';
import { Alert } from '@/utils/alert';
import { friendlyApiMessage } from '@/utils/api-error';
import { backOrInbox } from '@/utils/router-back';

/**
 * Owns the open composer, ABOVE the router.
 *
 * A composer used to be rendered by the page that opened it — `message-pane.tsx` for reply
 * and forward. `Portal` moves where the window PAINTS, not where it lives in the React tree,
 * so navigating away unmounted the page, the portal and the draft with it: minimize a reply,
 * click Dashboard, and a half-written letter was gone with no warning. Mounted here, beside
 * `GlobalSearchPopup` and outside the `Drawer`, the window survives every navigation.
 *
 * Refresh is the other half, and storage handles that — the composer records WHICH draft is
 * open (below) and each composer persists its own text via `useComposerDraft`.
 *
 * One composer at a time, which is the decision the app already made: opening a second
 * replaces the first. Two windows would dock at identical coordinates and hide each other,
 * and each would register its own Cmd+Enter listener.
 */

/**
 * Where to send the reader once a reply ARCHIVES its message, decided by the page that
 * opened the composer because only it knows both halves.
 *
 * Serializable on purpose: it rides in the stored `composer-open` record, so it survives the
 * reload that the draft survives. A callback could not — the page that registered it is long
 * unmounted by then.
 */
export interface AfterArchive {
	/** A linked exterior-application request, which outranks the inbox. */
	href?: string | null;
	/** The inbox label the reader came from, so they land back in that queue. */
	label?: string | null;
}

/**
 * The collections letter is the odd one out: it is composed from a QUEUE ROW, not fetched
 * from a record id, so everything the letter merges has to travel with the request. All six
 * fields are carried (rather than re-fetched from `accountId`) so a restored letter shows the
 * same balance and address it was opened with — re-deriving them would quietly change the
 * numbers in a notice the reader had already reviewed.
 */
export interface CollectionsLetterTarget {
	accountId: string;
	associationId: string;
	accountName: string;
	address?: string | null;
	balanceCents: number;
	daysOverdue?: number | null;
}

export type ComposerRequest =
	| { kind: 'reply'; messageId: string; afterArchive?: AfterArchive }
	| { kind: 'forward'; messageId: string }
	| { kind: 'needs-info'; applicationId: string }
	| { kind: 'board-approval'; applicationId: string }
	| ({ kind: 'collections-letter' } & CollectionsLetterTarget);

/** The record a request is about — its draft key, and what a refresh needs to restore it. */
const requestId = (request: ComposerRequest): string => {
	if (request.kind === 'needs-info' || request.kind === 'board-approval') return request.applicationId;
	if (request.kind === 'collections-letter') return request.accountId;
	return request.messageId;
};

const sameRequest = (a: ComposerRequest, b: ComposerRequest): boolean =>
	a.kind === b.kind && requestId(a) === requestId(b);

/** How each composer is named in the discard prompt. Written to read inside a sentence. */
const REQUEST_LABEL: Record<ComposerRequest['kind'], string> = {
	reply: 'your reply',
	forward: 'this forward',
	'needs-info': 'your letter to the homeowner',
	'board-approval': 'your Waiting for Board Approval letter',
	'collections-letter': 'this collections letter',
};

/**
 * Is a stored record's `kind` one this build understands?
 *
 * Derived from `REQUEST_LABEL` rather than hand-listed, and that is the whole point. The
 * check used to be `kind === 'reply' || kind === 'forward' || kind === 'needs-info'`, so
 * adding the collections letter compiled clean and then silently refused to restore its
 * drafts — a reload just dropped them. `REQUEST_LABEL` is a
 * `Record<ComposerRequest['kind'], string>`, so a new variant is a TYPE ERROR until it is
 * added there, and this function picks it up for free.
 */
const isKnownKind = (kind: unknown): kind is ComposerRequest['kind'] =>
	typeof kind === 'string' && Object.prototype.hasOwnProperty.call(REQUEST_LABEL, kind);

/** Storage key for a request's draft. Must match what `useComposerDraft` writes. */
const draftKeyFor = (userId: string, request: ComposerRequest): string =>
	draftStorageKey(userId, `${request.kind}:${requestId(request)}`);

/**
 * Which composer was open, so a refresh can bring it back. Drafts are stored separately.
 *
 * Scoped by user id for the same reason the drafts are: `authService.signOut()` deletes three
 * named keys and leaves the rest of storage alone, so an unscoped record let the next person
 * to sign in on a shared browser restore — and send — the previous user's correspondence.
 */
const OPEN_PREFIX = 'composer-open:';
const openKeyFor = (userId: string): string => `${OPEN_PREFIX}${userId}`;

interface ComposerApi {
	open: (request: ComposerRequest) => void;
	close: () => void;
	current: ComposerRequest | null;
}

const ComposerContext = createContext<ComposerApi | null>(null);

export const useComposer = (): ComposerApi => {
	const api = useContext(ComposerContext);
	if (!api) throw new Error('useComposer must be used inside <ComposerProvider>');
	return api;
};

export const ComposerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [current, setCurrent] = useState<ComposerRequest | null>(null);
	/**
	 * A restored composer comes back COLLAPSED.
	 *
	 * After a reload the user asked for a page, not for a letter they were writing an hour
	 * ago — reopening it full size over the top of what they navigated to would be hostile.
	 * A title bar docked at the bottom edge says "your draft is still here" and gets out of
	 * the way, which is the same thing minimize means everywhere else.
	 */
	const [startMinimized, setStartMinimized] = useState(false);
	const { showToast, showModal } = useNotification();
	const pathname = usePathname();
	const { user } = useSession();
	const userId = user?.id ?? null;

	/**
	 * Whoever is signed in now owns the only composer state that may exist.
	 *
	 * Runs on every identity change, including sign-out (`userId === null`), which clears
	 * everything. Without it a draft written by the previous user sits in storage forever,
	 * because sign-out only deletes its own three keys.
	 */
	useEffect(() => {
		void purgeForeignComposerDrafts(userId, OPEN_PREFIX);
	}, [userId]);

	// Anything open belongs to the previous user; drop it rather than carry it across.
	useEffect(() => {
		setCurrent(null);
		setStartMinimized(false);
	}, [userId]);

	useEffect(() => {
		if (!userId) return;
		let cancelled = false;
		void (async () => {
			try {
				const raw = await AsyncStorage.getItem(openKeyFor(userId));
				if (cancelled || !raw) return;
				const parsed = JSON.parse(raw) as ComposerRequest;
				// A record written by an older build, or with its id missing, must not open an
				// empty composer pointing at nothing.
				if (!isKnownKind(parsed?.kind) || !requestId(parsed)) return;
				setStartMinimized(true);
				setCurrent(parsed);
			} catch {
				// A corrupt record must never block the app booting.
			}
		})();
		return () => { cancelled = true; };
	}, [userId]);

	/**
	 * Whether the open composer holds work the user would miss.
	 *
	 * A ref, not state: it is read inside `open`/`close` at the moment of the click, and
	 * re-rendering the whole app every time someone types a character would be absurd. The
	 * active composer pushes its own dirty flag up through `onDirtyChange`.
	 */
	const dirty = useRef(false);
	/**
	 * Set by an `onSent` handler immediately before the composer calls `onDismiss`.
	 *
	 * Without it the post-send close would prompt "discard this draft?" about a letter that
	 * was just successfully sent — the composer's `dirty` flag is still true at that instant,
	 * because clearing it is a state update that has not been applied yet.
	 */
	const justSent = useRef(false);
	/** Bumped on every explicit open, so re-clicking Reply expands an already-minimized one. */
	const [expandRequest, setExpandRequest] = useState(0);

	const reallyOpen = useCallback((request: ComposerRequest) => {
		dirty.current = false;
		setStartMinimized(false);
		setExpandRequest((n) => n + 1);
		setCurrent(request);
		if (userId) void AsyncStorage.setItem(openKeyFor(userId), JSON.stringify(request)).catch(() => {});
	}, [userId]);

	const reallyClose = useCallback(() => {
		dirty.current = false;
		setCurrent(null);
		setStartMinimized(false);
		if (userId) void AsyncStorage.removeItem(openKeyFor(userId)).catch(() => {});
	}, [userId]);

	/**
	 * Opening a second composer REPLACES the first — so say so before throwing work away.
	 *
	 * One composer at a time is the design, and until now the swap was silent: a half-written
	 * letter to a homeowner disappeared the moment someone clicked Reply on a message they
	 * happened to be looking at. Re-opening the SAME composer is not a replacement and must
	 * not prompt; it just brings it back to the front.
	 */
	const open = useCallback((request: ComposerRequest) => {
		if (current && sameRequest(current, request)) {
			setExpandRequest((n) => n + 1);
			return;
		}
		if (current && dirty.current) {
			const outgoing = current;
			Alert.alert(
				'Discard this draft?',
				`You have unsaved changes in ${REQUEST_LABEL[outgoing.kind]}. `
				+ `Opening ${REQUEST_LABEL[request.kind]} will discard them.`,
				[
					{ text: 'Keep editing', style: 'cancel' },
					{
						text: 'Discard',
						style: 'destructive',
						onPress: () => {
							// Discard means discard: leaving the stored copy behind would strand a
							// draft nothing can ever reopen.
							if (userId) void AsyncStorage.removeItem(draftKeyFor(userId, outgoing)).catch(() => {});
							reallyOpen(request);
						},
					},
				],
			);
			return;
		}
		reallyOpen(request);
	}, [current, userId, reallyOpen]);

	/** Closing by ✕ or Cancel destroys the draft too, so it gets the same prompt. */
	const close = useCallback(() => {
		if (justSent.current) { justSent.current = false; reallyClose(); return; }
		if (current && dirty.current) {
			const outgoing = current;
			Alert.alert(
				'Discard this draft?',
				`You have unsaved changes in ${REQUEST_LABEL[outgoing.kind]}. Closing will discard them.`,
				[
					{ text: 'Keep editing', style: 'cancel' },
					{
						text: 'Discard',
						style: 'destructive',
						onPress: () => {
							if (userId) void AsyncStorage.removeItem(draftKeyFor(userId, outgoing)).catch(() => {});
							reallyClose();
						},
					},
				],
			);
			return;
		}
		reallyClose();
	}, [current, userId, reallyClose]);

	const onDirtyChange = useCallback((value: boolean) => { dirty.current = value; }, []);

	const api = useMemo<ComposerApi>(() => ({ open, close, current }), [open, close, current]);

	const fail = useCallback((error: unknown, fallback: string) => {
		showModal({ title: 'Something went wrong', message: friendlyApiMessage(error, fallback), variant: 'error' });
	}, [showModal]);

	/**
	 * The OUTCOME is reported here, and it distinguishes a real send from a gated one. One
	 * cheerful "Sent" toast for both is exactly how someone comes to believe a homeowner was
	 * emailed when nothing left the building.
	 */
	const reportGated = useCallback((title: string, reason: string | null | undefined, detail: string) => {
		showModal({
			title,
			message: `${reason ?? 'Email sending is off in this environment.'}\n\n${detail}`,
			variant: 'warning',
		});
	}, [showModal]);

	return (
		<ComposerContext.Provider value={api}>
			{children}

			{current?.kind === 'reply' ? (
				<ReplyModal
					key={`reply:${current.messageId}`}
					messageId={current.messageId}
					visible
					startMinimized={startMinimized}
					expandRequest={expandRequest}
					onDirtyChange={onDirtyChange}
					draftScope={userId}
					onDismiss={close}
					onError={fail}
					onSent={(result) => {
						// The composer calls onDismiss straight after this; without the flag the
						// close below would ask whether to discard a letter that just went out.
						justSent.current = true;
						if (result.sent) {
							showToast(result.movedTo ? 'Replied and archived' : 'Reply sent', { variant: 'success' });
							/*
								Only navigate if the sender is STILL LOOKING at the message that just
								got archived. The composer now outlives its page, so a reply sent from
								the dashboard used to yank the user into the inbox — the composer's job
								is to send the letter, not to decide where they are working.
							*/
							/*
								The destination comes from `afterArchive`, captured when the composer was
								opened. Moving this out of `message-pane` had lost BOTH halves of what its
								`goBack()` did: a linked improvement request outranks the inbox, and failing
								that the reader returns to the LABEL they came from rather than the unscoped
								list — `?label=triage` on a refreshed deep link takes the `backOrInbox`
								fallback path and dropped the queue.
							*/
							if (result.movedTo && pathname.startsWith(`/inbox/${current.messageId}`)) {
								const after = current.afterArchive;
								if (after?.href) router.push(after.href as Href);
								else backOrInbox(after?.label ?? null);
							}
							return;
						}
						reportGated(
							'Recorded, not emailed',
							result.reason,
							'The reply is saved on the thread, but nothing was sent'
							+ `${result.movedTo ? ' — and this message was archived.' : '.'}`,
						);
					}}
				/>
			) : null}

			{current?.kind === 'collections-letter' ? (
				<CollectionsLetterModal
					key={`collections-letter:${current.accountId}`}
					accountId={current.accountId}
					associationId={current.associationId}
					accountName={current.accountName}
					address={current.address ?? undefined}
					balanceCents={current.balanceCents}
					daysOverdue={current.daysOverdue ?? undefined}
					visible
					startMinimized={startMinimized}
					expandRequest={expandRequest}
					onDirtyChange={onDirtyChange}
					draftScope={userId}
					onDismiss={close}
					onLogged={() => {
						// Same one-shot guard the sends use: the modal calls onDismiss straight
						// after this, and its dirty flag is still set at that instant.
						justSent.current = true;
						showToast('Letter logged as sent', { variant: 'success' });
					}}
				/>
			) : null}

			{current?.kind === 'needs-info' ? (
				<NeedsInfoModal
					key={`needs-info:${current.applicationId}`}
					applicationId={current.applicationId}
					visible
					startMinimized={startMinimized}
					expandRequest={expandRequest}
					onDirtyChange={onDirtyChange}
					draftScope={userId}
					onDismiss={close}
					onMoveError={(error) => fail(error, 'Could not move the request. Nothing changed.')}
					onMovedWithoutSending={() => {
						// Closing on purpose: skip the discard prompt the open draft would raise.
						justSent.current = true;
						showToast('Moved to Needs Additional Info — no email sent', { variant: 'success' });
					}}
					onSent={(result) => {
						// The composer calls onDismiss straight after this; without the flag the
						// close below would ask whether to discard a letter that just went out.
						justSent.current = true;
						/*
							THREE outcomes, not two.

							A skip is the normal state until outbound email is switched on, and a green
							toast for it would leave staff believing a homeowner was told. But a
							transport FAILURE is not a skip: the server writes nothing, the status does
							not move, and the homeowner has nothing. Reporting that as "recorded, not
							emailed" told the reviewer three things that were all false and left them
							with no reason to retry.
						*/
						const { sent, skipped, error } = result.emailResult;
						if (sent) {
							showToast('Letter sent to the homeowner', { variant: 'success' });
						} else if (!skipped) {
							showModal({
								title: 'Could not send',
								message: `${error ?? 'The email could not be sent.'}\n\n`
									+ 'Nothing was recorded and the request has not moved. Try again.',
								variant: 'error',
							});
						} else {
							reportGated(
								'Recorded, not emailed',
								error ?? 'Outbound email is switched off.',
								result.statusChanged
									? 'The letter is on the request and the status has moved, but nothing was sent.'
									: 'The letter is on the request, but nothing was sent.',
							);
						}
					}}
				/>
			) : null}

			{current?.kind === 'board-approval' ? (
				<BoardApprovalModal
					key={`board-approval:${current.applicationId}`}
					applicationId={current.applicationId}
					visible
					startMinimized={startMinimized}
					expandRequest={expandRequest}
					onDirtyChange={onDirtyChange}
					draftScope={userId}
					onDismiss={close}
					onError={(error) => fail(error, 'The letter could not be sent. Nothing was recorded.')}
					onMoveError={(error) => fail(error, 'Could not move the request. Nothing changed.')}
					onMovedWithoutSending={() => {
						// Closing on purpose: skip the discard prompt the open draft would raise.
						justSent.current = true;
						showToast('Moved to Waiting for Board Approval — no letter sent', { variant: 'success' });
					}}
					onSent={(result) => {
						const { sent, skipped, error } = result.emailResult;
						/*
							A failure keeps the composer OPEN (a render failure is the likely one,
							and retyping the letter would be miserable), so the post-send flag is
							only raised when the modal is about to close. Raised on a failure it
							would outlive this call and skip the discard prompt on the next ✕.
						*/
						if (sent || skipped) justSent.current = true;
						const attached = result.attachment ? ` with ${result.attachment.fileName} attached` : '';
						if (sent && result.emailResult.sunkTo) {
							// Really sent — to the preview test mailbox. A green "sent" here would tell
							// the reviewer the homeowner has it.
							reportGated(
								'Sent to the test mailbox',
								`This environment sends every email to ${result.emailResult.sunkTo}.`,
								`The letter${attached} went there, NOT to the homeowner. It is recorded on the request.`,
							);
						} else if (sent) {
							showToast(`Letter sent${attached}`, { variant: 'success' });
						} else if (!skipped) {
							showModal({
								title: 'Could not send',
								message: `${error ?? 'The email could not be sent.'}\n\n`
									+ 'Nothing was recorded and the request has not moved. Your letter is still open — try again.',
								variant: 'error',
							});
						} else {
							reportGated(
								'Recorded, not emailed',
								error ?? 'Outbound email is switched off.',
								result.statusChanged
									? `The letter${attached} is on the request and the status has moved, but nothing was sent.`
									: `The letter${attached} is on the request, but nothing was sent.`,
							);
						}
					}}
				/>
			) : null}

			{current?.kind === 'forward' ? (
				<ForwardModal
					key={`forward:${current.messageId}`}
					messageId={current.messageId}
					visible
					startMinimized={startMinimized}
					expandRequest={expandRequest}
					onDirtyChange={onDirtyChange}
					draftScope={userId}
					onDismiss={close}
					onError={fail}
					onSent={(result) => {
						// The composer calls onDismiss straight after this; without the flag the
						// close below would ask whether to discard a letter that just went out.
						justSent.current = true;
						if (result.sent) {
							showToast('Forwarded', { variant: 'success' });
							return;
						}
						reportGated(
							'Recorded, not emailed',
							result.reason,
							'The forward is saved on the thread, but nothing was sent.',
						);
					}}
				/>
			) : null}
		</ComposerContext.Provider>
	);
};
